import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  HIRING_REGION_PRESETS,
  inferRegionFromLocation,
  parseRegions,
  regionOptions,
  toggleRegion,
} from "@/lib/hiring-regions"

function RegionPill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors",
        active
          ? "border-transparent bg-[rgb(196_240_75/0.55)] text-[color:var(--wf-ink)]"
          : "border-[rgb(12_15_20/0.12)] bg-white text-[color:var(--wf-mute)] hover:bg-[rgb(243_251_227/0.7)]",
      )}
    >
      {children}
    </button>
  )
}

type MultiProps = {
  variant?: "multi"
  value: string
  onChange: (value: string) => void
  label?: string
  description?: string
  className?: string
}

type SingleProps = {
  variant: "single"
  value: string
  onChange: (value: string) => void
  label?: string
  description?: string
  optional?: boolean
  control?: "pills" | "select"
  emptyLabel?: string
  className?: string
}

export type HiringRegionPickerProps = MultiProps | SingleProps

export function HiringRegionPicker(props: HiringRegionPickerProps) {
  const label = props.label ?? "Area"
  const description = props.description

  if (props.variant === "single") {
    const {
      value,
      onChange,
      optional = true,
      control = "pills",
      emptyLabel,
      className,
    } = props
    const active =
      HIRING_REGION_PRESETS.includes(value as (typeof HIRING_REGION_PRESETS)[number])
        ? value
        : inferRegionFromLocation(value)

    return (
      <div className={cn("space-y-2", className)}>
        <Label>{label}</Label>
        {description && (
          <p className="text-xs text-[color:var(--wf-mute)]">{description}</p>
        )}
        {control === "select" ? (
          <Select
            value={active || (emptyLabel ? "any" : undefined)}
            onValueChange={(next) => {
              if (next === "any") {
                onChange("")
                return
              }
              // Same Radix-in-<form> empty change as FormSelect.
              if (!next) return
              onChange(next)
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {emptyLabel && (
                <SelectItem value="any">{emptyLabel}</SelectItem>
              )}
              {HIRING_REGION_PRESETS.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex flex-wrap gap-2">
            {HIRING_REGION_PRESETS.map((region) => (
              <RegionPill
                key={region}
                active={active === region}
                onClick={() => {
                  if (active === region && optional) {
                    onChange("")
                    return
                  }
                  onChange(region)
                }}
              >
                {region}
              </RegionPill>
            ))}
          </div>
        )}
      </div>
    )
  }

  const { value, onChange, className } = props
  const selected = parseRegions(value)
  const options = regionOptions(value)

  return (
    <div className={cn("space-y-2", className)}>
      <Label>{label}</Label>
      {description && (
        <p className="text-xs text-[color:var(--wf-mute)]">{description}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((region) => (
          <RegionPill
            key={region}
            active={selected.includes(region)}
            onClick={() => onChange(toggleRegion(value, region))}
          >
            {region}
          </RegionPill>
        ))}
      </div>
    </div>
  )
}

export function HiringRegionChips({
  regions,
  className,
}: {
  regions: string[]
  className?: string
}) {
  if (regions.length === 0) return null
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {regions.map((region) => (
        <span key={region} className="mkt-chip mkt-chip--outline">
          {region}
        </span>
      ))}
    </div>
  )
}
